import React from 'react';
import { Info, Layout, Target, ArrowUp, BookOpen } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ClosingTheInterviewGuide = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Closing the Interview Guide</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Making a Strong Final Impression</h2>
              <p className="text-gray-700">
                Making a strong impression at the end of an interview is just as crucial as how you start. 
                This guide will help you master the art of closing interviews with confidence and professionalism.
              </p>
            </div>
          </div>
        </div>

        {/* Why Closing Matters */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Target className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. Why Closing Matters</h3>
              <ul className="space-y-4">
                <li>The closing of an interview reinforces your enthusiasm for the role</li>
                <li>It provides an opportunity to address any last-minute concerns and clarify next steps</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Components */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Key Components of a Strong Closing</h3>
              
              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Express Gratitude</h4>
                <div className="bg-white p-4 rounded-lg mb-2">
                  <p className="text-gray-600 italic">
                    "Thank you for taking the time to meet with me today. I've enjoyed learning more about the role and the company."
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Reiterate Interest</h4>
                <div className="bg-white p-4 rounded-lg mb-2">
                  <p className="text-gray-600 italic">
                    "I'm very excited about the opportunity to contribute to your team and help achieve your goals."
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Ask About Next Steps</h4>
                <div className="bg-white p-4 rounded-lg mb-2">
                  <p className="text-gray-600 italic">
                    "Could you share what the next steps in the hiring process are?"
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Offer Additional Information</h4>
                <div className="bg-white p-4 rounded-lg mb-2">
                  <p className="text-gray-600 italic">
                    "If you need any additional information or references, please don't hesitate to let me know."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Scripts */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Closing Scripts</h3>
              
              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Single-Interviewer Script</h4>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <p className="text-gray-600 italic">
                    "Steve, thank you so much for meeting with me today. I'm very interested in the Director of Operations position 
                    and believe my skills and experience align well with what you're looking for. What are the next steps in the 
                    hiring process?"
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">Multi-Interviewer Script</h4>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <p className="text-gray-600 italic">
                    "Thank you all for taking the time to meet with me. I'm very interested in joining your team and contributing 
                    to [specific project or goal discussed]. I'd love to hear about the next steps in your hiring process."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Handling Objections */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <ArrowUp className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">4. Handling Objections or Concerns</h3>
              <div className="bg-white p-4 rounded-lg mb-4">
                <p className="text-gray-600 italic">
                  "I understand your concern about my experience with [specific skill]. Let me share how I've been working 
                  to develop that skill through [specific course or project]."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Follow-Up Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Final Touch: Follow-Up</h3>
              <p className="mb-4">Send a thank-you email within 24 hours, reiterating your interest and key takeaways.</p>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium mb-2">Example Email:</p>
                <p className="text-gray-600 italic">
                  Dear [Interviewer's Name],<br /><br />
                  Thank you for meeting with me today. I'm very excited about the opportunity to join [Company Name] 
                  and contribute to [specific goal]. Please let me know if I can provide any additional information.<br /><br />
                  Best regards,<br />
                  [Your Name]
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By closing interviews effectively, you'll leave a lasting positive impression and increase your chances 
            of advancing in the hiring process. Practice these techniques to build confidence and stand out!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClosingTheInterviewGuide;